import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import InfiniteScroll from 'react-infinite-scroll-component';
import { fetchProducts, verifyProduct } from '../utils/api';

const Home = () => {
  const [scratchCode, setScratchCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationMessage, setVerificationMessage] = useState({ text: '', type: '' });

  const productsPerPage = 10;

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchProducts();
        setAllProducts(data);
        setDisplayedProducts(data.slice(0, productsPerPage));
        const uniqueCategories = [...new Set(data.map((p) => p.category))];
        setCategories(uniqueCategories);
        setPage(1);
        setHasMore(data.length > productsPerPage);
      } catch {
        setError('Failed to load products. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
    setIsMobile(window.innerWidth < 768);
  }, []);

  const [hasMore, setHasMore] = useState(true);

  const fetchMoreProducts = () => {
    const nextPage = page + 1;
    const startIndex = (nextPage - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    const newProducts = allProducts.slice(startIndex, endIndex);

    setDisplayedProducts((prev) => [...prev, ...newProducts]);
    setPage(nextPage);

    if (endIndex >= allProducts.length) {
      setHasMore(false);
    }
  };

  const filteredProducts = useMemo(() => {
    return displayedProducts.filter((product) => {
      const matchesSearch =
        searchQuery.trim() === '' || product.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === 'All Categories' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory, displayedProducts]);

  const handleVerification = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setVerificationMessage({ text: 'Please enter a verification code', type: 'error' });
      return;
    }

    try {
      setIsVerifying(true);
      const response = await verifyProduct(verificationCode.trim());
      setVerificationMessage({ text: response.message, type: response.status });
      setVerificationCode('');
    } catch {
      setVerificationMessage({
        text: 'Failed to verify product. Please try again.',
        type: 'error',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className='rounded-lg max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 bg-gray-500 mb-2 text-gray-800 font-sans mt-12'>
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className='flex flex-col md:flex-row justify-between items-start mb-10 gap-6'
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
          }}
          className='flex flex-wrap gap-3 flex-1 min-w-0'
        >
          <input
            type='search'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search products...'
            className='flex-grow min-w-[150px] p-2 text-sm bg-gray-100 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition'
          />
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setPage(1);
            }}
            className='w-44 p-2 text-sm bg-gray-100 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition'
          >
            <option value='All Categories'>All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <button
            type='submit'
            className='bg-primary hover:bg-primary-dark active:scale-95 transition-transform text-white text-sm font-semibold px-4 rounded-md shadow'
          >
            Search
          </button>
        </form>

        <form onSubmit={handleVerification} className='flex gap-3 w-full max-w-xs'>
          <input
            type='search'
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder='Enter code'
            className='flex-grow p-2 text-sm bg-gray-100 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition'
          />
          <button
            type='submit'
            className='bg-primary hover:bg-primary-dark active:scale-95 transition-transform text-white text-sm font-semibold px-4 rounded-md shadow'
            disabled={isVerifying}
          >
            Verify
          </button>
        </form>
      </motion.section>

      {verificationMessage.text && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-6 p-3 text-sm rounded-md max-w-xl mx-auto ${
            verificationMessage.type === 'success'
              ? 'bg-green-100 text-green-800 border-l-4 border-green-500'
              : 'bg-red-100 text-red-800 border-l-4 border-red-500'
          }`}
        >
          {verificationMessage.text}
        </motion.div>
      )}

      <section>
        {error && (
          <div className='mb-4 p-3 text-sm bg-red-100 text-red-800 border-l-4 border-red-500 rounded-md max-w-4xl mx-auto'>
            {error}
          </div>
        )}

        {loading ? (
          <div className='text-center py-12 text-gray-500 text-sm'>Loading...</div>
        ) : filteredProducts.length === 0 ? (
          <div className='text-center py-12 text-gray-500 text-sm'>
            No products found matching your criteria.
          </div>
        ) : (
          <InfiniteScroll
            dataLength={filteredProducts.length}
            next={fetchMoreProducts}
            hasMore={hasMore}
            loader={<p className='text-center text-sm py-4 text-gray-500'>Loading more...</p>}
          >
            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto'>
              {filteredProducts.map((product) => (
                <motion.div
                  key={product.id}
                  whileHover={{ scale: 1.05, boxShadow: '0 8px 15px rgba(0,0,0,0.1)' }}
                  className='bg-white border border-gray-200 rounded-lg p-3 cursor-pointer transition-shadow'
                >
                  <div className='aspect-w-1 aspect-h-1 mb-3 overflow-hidden rounded-md'>
                    <img
                      src={`https://rgmeds.com/products/${product.image}`}
                      alt={product.name}
                      loading='lazy'
                      className='object-cover w-full h-full transform transition-transform duration-300 hover:scale-110'
                      onError={(e) => {
                        e.target.src = 'https://rgmeds.com/products/placeholder.jpg';
                      }}
                    />
                  </div>
                  <h3 className='text-md font-semibold mb-1 truncate'>{product.name}</h3>
                  <p className='text-primary text-sm font-medium'>{product.category}</p>
                </motion.div>
              ))}
            </div>
          </InfiniteScroll>
        )}
      </section>
    </div>
  );
};

export default Home;
